import ConciliationStatusChip, {
  rowConciliationStatus,
  conciliationStatus,
} from "modules/finops/conciliaciones/components/ConciliationStatusChip";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  CircularProgress,
  Alert,
  Snackbar,
  Card,
  Box,
  Grid,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Refresh,
  Download,
  Visibility,
  Close,
  PictureAsPdf,
  Add,
  Assessment,
  AttachMoney,
  FilterAlt,
  TrendingUp,
  TrendingDown,
  CheckCircle,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import * as XLSX from "xlsx";

import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import DataTable from "shared/components/Tables/DataTable";
import { getConciliationFilesById } from "modules/finops/conciliaciones/services/financieraService";
import useLoteRecords from "../../hooks/useLoteRecords";
import TableDetalleFinanciera1 from "./TableDetalleFinanciera1";
import ModalPDF from "modules/finops/conciliaciones/lotesFinanciera/components/ModalPDF";
import ModalAgregarNovedad from "../Modals/ModalAgregarNovedad";
import ModalConciliar from "../Modals/ModalConciliar";
import { matchesLoteStatus, getLoteStatusOptions } from "../../loteStatusOptions";

const novedadesBloqueadas = (row) =>
  ["pagado", "pagada", "conciliado-liquidar", "auto-liquidado", "auto-liquidada"].includes(
    String(row?.status || "")
      .trim()
      .toLowerCase()
      .replace(/[\s_]+/g, "-")
  );

const colors = {
  primary: "#1e293b",
  secondary: "#0b4f6c",
  white: "#ffffff",
  success: "#27ae60",
  successDark: "#1e7e34",
  warning: "#da1c15ff",
  info: "#2980b9",
  pdf: "#e74c3c",
  email: "#1a73e8",
  emailDisabled: "#9e9e9e",
  orange: "#e67e22",
  red: "#c0392b",
  redDark: "#a93226",
  excel: "#1e7145",
  excelLight: "#e8f5e9",
};

// ================================================
// HELPERS
// ================================================
const formatFecha = (value) => {
  if (!value) return "N/A";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("es-CO", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatMoney = (v) =>
  `$${Number(v || 0).toLocaleString("es-CO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

// ================================================
// CELDAS
// ================================================
const LlaveCell = ({ value }) => (
  <Chip
    label={value || "N/A"}
    size="small"
    sx={{
      fontWeight: 600,
      fontFamily: "monospace",
      backgroundColor: value ? `${colors.info}15` : "#f5f5f5",
      color: value ? colors.info : "#999",
      border: value ? `1px solid ${colors.info}30` : "1px solid #ddd",
    }}
  />
);
LlaveCell.propTypes = { value: PropTypes.string };
LlaveCell.defaultProps = { value: "" };

const VerticalCell = ({ value }) => (
  <Chip
    label={value || "N/A"}
    size="small"
    sx={{
      fontWeight: 600,
      backgroundColor: value ? `${colors.success}15` : "#f5f5f5",
      color: value ? colors.success : "#999",
    }}
  />
);
VerticalCell.propTypes = { value: PropTypes.string };
VerticalCell.defaultProps = { value: "" };

const ObservacionCell = ({ value }) => (
  <MDTypography variant="caption" sx={{ fontSize: "0.8rem" }}>
    {value || "N/A"}
  </MDTypography>
);
ObservacionCell.propTypes = { value: PropTypes.string };
ObservacionCell.defaultProps = { value: "" };

// ================================================
// WRAPPERS
// ================================================
const LlaveWrapper = ({ row }) => (
  <LlaveCell value={row.original.key_concil || row.original.llave} />
);
LlaveWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      key_concil: PropTypes.string,
      llave: PropTypes.string,
    }),
  }).isRequired,
};

const VerticalWrapper = ({ row }) => (
  <VerticalCell value={row.original.vertical || row.original.realName} />
);
VerticalWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      vertical: PropTypes.string,
      realName: PropTypes.string,
    }),
  }).isRequired,
};

const StatusWrapper = ({ row }) => <ConciliationStatusChip value={row.original.status} />;
StatusWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      estado_cuadre: PropTypes.string,
      status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
};

const StatusTextWrapper = ({ row }) => <ConciliationStatusChip value={row.original.status} />;
StatusTextWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      status: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
};

const ObservacionWrapper = ({ row }) => <ObservacionCell value={row.original.observation} />;
ObservacionWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      observation: PropTypes.string,
    }),
  }).isRequired,
};

const IdConciliacionWrapper = ({ row }) => (
  <MDTypography variant="caption">
    {row.original.conciliation_id || row.original.id || "N/A"}
  </MDTypography>
);
IdConciliacionWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      conciliation_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
};

const UuidWrapper = ({ row }) => (
  <MDTypography
    variant="caption"
    sx={{ fontFamily: "monospace", fontSize: "0.75rem", wordBreak: "break-all" }}
  >
    {row.original.uuid || "N/A"}
  </MDTypography>
);
UuidWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      uuid: PropTypes.string,
    }),
  }).isRequired,
};

const FechaCreacionWrapper = ({ row }) => (
  <MDTypography variant="caption" sx={{ fontSize: "0.8rem" }}>
    {formatFecha(row.original.created_at)}
  </MDTypography>
);
FechaCreacionWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      created_at: PropTypes.string,
    }),
  }).isRequired,
};

const CountConcilWrapper = ({ row }) => (
  <MDTypography variant="caption" fontWeight="600">
    {row.original.countconcil != null
      ? Number(row.original.countconcil).toLocaleString("es-CO")
      : "N/A"}
  </MDTypography>
);
CountConcilWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      countconcil: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
};

const valorGopass = (value) =>
  value != null && String(value).trim() !== "" && Number.isFinite(Number(value))
    ? Number(value)
    : null;
const ValorGopassWrapper = ({ row }) => (
  <MDTypography variant="caption" fontWeight="600">
    {valorGopass(row.original.value_gp) == null ? "—" : formatMoney(row.original.value_gp)}
  </MDTypography>
);
ValorGopassWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      value_gp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
};

const ConcesionWrapper = ({ row }) => (
  <MDTypography variant="caption" fontWeight="600">
    {row.original.concesion || "N/A"}
  </MDTypography>
);
ConcesionWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      concesion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
};

const NombreWrapper = ({ row }) => (
  <MDTypography variant="caption" fontWeight="600" sx={{ fontSize: "0.8rem" }}>
    {String(row.original.name || row.original.realName || "N/A").toUpperCase()}
  </MDTypography>
);
NombreWrapper.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      name: PropTypes.string,
      realName: PropTypes.string,
    }),
  }).isRequired,
};
const paymentTypeLabel = (value) => {
  const type = String(value || "")
    .trim()
    .toUpperCase();
  return type === "POR_PEAJE"
    ? "Por peaje"
    : type === "CONSOLIDADO"
    ? "Consolidado"
    : type || "No informado";
};
const TipoPagoWrapper = ({ row }) => (
  <MDTypography variant="caption" fontWeight="600" sx={{ color: "#0f172a" }}>
    {paymentTypeLabel(row.original.tipo_pago)}
  </MDTypography>
);
TipoPagoWrapper.propTypes = {
  row: PropTypes.shape({ original: PropTypes.shape({ tipo_pago: PropTypes.string }) }).isRequired,
};

// ================================================
// ACCIONES
// ================================================
const AccionesCell = ({
  row,
  modo,
  onVerDetalle,
  onVerPDF,
  onFiltrar,
  onAgregarNovedad,
  onConciliar,
}) => {
  const idConcil = row.original.conciliation_id || row.original.id;
  const keyConcil = row.original.key_concil || row.original.llave || "";
  const codeOp = row.original.concesion ?? row.original.code_op ?? "";
  const tieneValorFinal =
    row.original.value_final != null && String(row.original.value_final).trim() !== "";
  const tieneValorConcesion =
    row.original.valor_concesion != null && String(row.original.valor_concesion).trim() !== "";
  const porPeaje =
    String(row.original.tipo_pago || "")
      .trim()
      .toUpperCase() === "POR_PEAJE";
  const mostrarConciliar = porPeaje
    ? conciliationStatus(row.original.status).label === "En conciliación" && !tieneValorFinal
    : String(rowConciliationStatus(row.original)).trim().toUpperCase() === "CON_DIFERENCIA";

  const puedeConsultarArchivos = !!idConcil && String(codeOp).trim() !== "";

  const [files, setFiles] = useState([]);
  const [checkingFiles, setCheckingFiles] = useState(false);

  useEffect(() => {
    if (modo !== "llave" || !puedeConsultarArchivos) {
      setFiles([]);
      setCheckingFiles(false);
      return undefined;
    }

    let active = true;
    setCheckingFiles(true);

    getConciliationFilesById(idConcil, codeOp)
      .then((result) => {
        if (!active) return;
        setFiles(result.success ? result.data || [] : []);
      })
      .catch((error) => {
        console.error("Error verificando archivos:", error);
        if (active) setFiles([]);
      })
      .finally(() => {
        if (active) setCheckingFiles(false);
      });

    return () => {
      active = false;
    };
  }, [modo, idConcil, codeOp, puedeConsultarArchivos]);

  const tieneArchivos = files.length > 0;

  const handleVerPDF = useCallback(() => {
    if (!tieneArchivos) return;
    onVerPDF(row.original, files);
  }, [tieneArchivos, files, onVerPDF, row.original]);

  const handleAgregarNovedad = useCallback(() => {
    if (!onAgregarNovedad || novedadesBloqueadas(row.original)) return;
    onAgregarNovedad(row.original);
  }, [onAgregarNovedad, row.original]);

  const handleConciliar = useCallback(() => {
    if (!onConciliar) return;
    onConciliar(row.original);
  }, [onConciliar, row.original]);

  if (modo === "global") {
    return (
      <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
        <Tooltip title={keyConcil ? `Filtrar por llave ${keyConcil}` : "Sin llave"}>
          <span>
            <Button
              variant="contained"
              size="small"
              startIcon={<FilterAlt />}
              onClick={() => keyConcil && onFiltrar && onFiltrar(String(keyConcil).trim())}
              disabled={!keyConcil}
              sx={{
                textTransform: "none",
                bgcolor: colors.primary,
                color: colors.white,
                "&:hover": { bgcolor: "#1a6fa0" },
                fontSize: "0.7rem",
                px: 1.5,
                py: 0.5,
                minWidth: "90px",
              }}
            >
              Filtrar
            </Button>
          </span>
        </Tooltip>
      </Box>
    );
  }

  const actionSx = {
    width: 32,
    height: 32,
    borderRadius: "8px",
    border: "1px solid",
    "& .MuiSvgIcon-root": { fontSize: 18 },
  };
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 32px)",
        gap: 0.75,
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Tooltip title="Ver detalle de la conciliación" arrow>
        <IconButton
          aria-label="Ver detalle de la conciliación"
          onClick={() => onVerDetalle(row.original)}
          sx={{
            ...actionSx,
            color: colors.primary,
            bgcolor: "rgba(30,41,59,0.06)",
            borderColor: "rgba(30,41,59,0.2)",
            "&:hover": { bgcolor: "rgba(30,41,59,0.13)" },
          }}
        >
          <Visibility />
        </IconButton>
      </Tooltip>
      <Tooltip
        title={
          novedadesBloqueadas(row.original)
            ? "No se permiten novedades en una conciliación autoliquidada, pagada o conciliada por liquidar"
            : keyConcil && codeOp
            ? `Agregar novedad para concesión ${codeOp}`
            : "Requiere llave y concesión"
        }
        arrow
      >
        <span>
          <IconButton
            aria-label="Agregar novedad"
            disabled={!keyConcil || !codeOp || novedadesBloqueadas(row.original)}
            onClick={handleAgregarNovedad}
            sx={{
              ...actionSx,
              color: colors.secondary,
              bgcolor: "rgba(11,79,108,0.07)",
              borderColor: "rgba(11,79,108,0.3)",
              "&:hover": { bgcolor: "rgba(11,79,108,0.14)" },
            }}
          >
            <Add />
          </IconButton>
        </span>
      </Tooltip>
      {mostrarConciliar && !tieneValorConcesion && (
        <Tooltip
          title={`Conciliar la concesión ${codeOp || ""} de la llave ${keyConcil || ""}`}
          arrow
        >
          <span>
            <IconButton
              aria-label="Conciliar"
              onClick={handleConciliar}
              disabled={!onConciliar || !keyConcil || !codeOp}
              sx={{
                ...actionSx,
                color: "success.main",
                bgcolor: "rgba(46,125,50,0.07)",
                borderColor: "rgba(46,125,50,0.3)",
                "&:hover": { bgcolor: "rgba(46,125,50,0.14)" },
              }}
            >
              <CheckCircle />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {checkingFiles && (
        <Box sx={{ width: 32, height: 32, display: "grid", placeItems: "center" }}>
          <CircularProgress
            size={16}
            aria-label="Consultando documentos"
            sx={{ color: colors.pdf }}
          />
        </Box>
      )}
      {!checkingFiles && tieneArchivos && (
        <Tooltip title={`Ver documentos adjuntos (${files.length})`} arrow>
          <IconButton
            aria-label={`Ver documentos (${files.length})`}
            onClick={handleVerPDF}
            sx={{
              ...actionSx,
              color: "error.main",
              bgcolor: "rgba(211,47,47,0.07)",
              borderColor: "rgba(211,47,47,0.3)",
              "&:hover": { bgcolor: "rgba(211,47,47,0.14)" },
            }}
          >
            <PictureAsPdf />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

AccionesCell.propTypes = {
  row: PropTypes.object.isRequired,
  modo: PropTypes.oneOf(["global", "llave"]),
  onVerDetalle: PropTypes.func.isRequired,
  onVerPDF: PropTypes.func.isRequired,
  onFiltrar: PropTypes.func,
  onAgregarNovedad: PropTypes.func,
  onConciliar: PropTypes.func,
};

AccionesCell.defaultProps = {
  modo: "llave",
  onFiltrar: null,
  onAgregarNovedad: null,
  onConciliar: null,
};

// ================================================
// COMPONENTE PRINCIPAL
// ================================================
const TableLoteFinanciera1 = ({
  refreshFlag,
  onError,
  onSelectLote,
  onFiltrarLlave,
  llaveSeleccionada = "",
  fechaInicio = "",
  fechaFin = "",
  filtroVertical = "",
  filtroBusqueda = "",
  filtroStatus = "todos",
  tipoFiltro = "global",
  onStatusOptionsChange,
}) => {
  const { allRecords, loading, error, keySummary, fetchRecords, fetchByKey } = useLoteRecords({
    llaveSeleccionada,
    fechaInicio,
    fechaFin,
    filtroVertical,
    filtroStatus: "todos",
    refreshFlag,
    onError,
  });

  const [openDetalle, setOpenDetalle] = useState(false);
  const [selectedLoteData, setSelectedLoteData] = useState(null);
  const [openPDFModal, setOpenPDFModal] = useState(false);
  const [selectedKeyConcil, setSelectedKeyConcil] = useState(null);
  const [selectedFilesData, setSelectedFilesData] = useState([]);

  const [openNovedadModal, setOpenNovedadModal] = useState(false);
  const [selectedNovedadData, setSelectedNovedadData] = useState(null);

  const [conciliationRow, setConciliationRow] = useState(null);
  useEffect(() => setConciliationRow(null), [llaveSeleccionada]);

  // Resultado del envío del valor a pagar
  const [avisoConciliar, setAvisoConciliar] = useState(null);

  const isKeyMode = !!(llaveSeleccionada && String(llaveSeleccionada).trim() !== "");

  // ================================================
  // FILTRADO EN CLIENTE
  // ================================================
  const searchRecords = useMemo(() => {
    let data = [...allRecords];

    if (filtroBusqueda && filtroBusqueda.trim() !== "") {
      const searchTerm = filtroBusqueda.toLowerCase().trim();
      data = data.filter((item) => {
        const searchableFields = [
          String(item.id || ""),
          String(item.uuid || ""),
          String(item.conciliation_id || ""),
          String(item.name || item.realName || ""),
          String(item.key_concil || ""),
          String(item.concesion || ""),
          String(item.status || ""),
        ];
        return searchableFields.some((field) => field.toLowerCase().includes(searchTerm));
      });
    }

    return data;
  }, [allRecords, filtroBusqueda]);
  const statusOptions = useMemo(() => getLoteStatusOptions(searchRecords), [searchRecords]);
  useEffect(() => {
    if (!loading) onStatusOptionsChange?.(statusOptions);
  }, [loading, statusOptions, onStatusOptionsChange]);
  const filteredRecords = useMemo(() => {
    const priority = (status) => {
      const label = conciliationStatus(status).label;
      if (label === "En conciliación") return 0;
      if (label === "Autoliquidado") return 1;
      if (label === "Conciliado · Por liquidar") return 2;
      return 3;
    };
    return searchRecords
      .filter((item) => matchesLoteStatus(item.status, filtroStatus))
      .sort((a, b) => priority(a.status) - priority(b.status));
  }, [searchRecords, filtroStatus]);

  const totalFiltered = filteredRecords.length;

  const handleVerDetalle = useCallback(
    (row) => {
      setSelectedLoteData(row);
      setOpenDetalle(true);
      if (onSelectLote) {
        onSelectLote(row.conciliation_id || row.id, row.concesion);
      }
    },
    [onSelectLote]
  );

  const handleFiltrar = useCallback(
    (key) => {
      if (!key) return;
      if (onFiltrarLlave) {
        onFiltrarLlave(key);
      } else {
        fetchByKey(key);
      }
    },
    [onFiltrarLlave, fetchByKey]
  );

  const handleCloseDetalle = useCallback(() => {
    setOpenDetalle(false);
    setSelectedLoteData(null);
  }, []);

  const handleVerPDF = useCallback((row, files) => {
    const keyConcil = row.key_concil || row.llave;
    setSelectedKeyConcil(keyConcil);
    setSelectedFilesData(files);
    setOpenPDFModal(true);
  }, []);

  const handleClosePDFModal = useCallback(() => {
    setOpenPDFModal(false);
    setSelectedKeyConcil(null);
    setSelectedFilesData([]);
  }, []);

  const handleAgregarNovedad = useCallback((row) => {
    if (novedadesBloqueadas(row)) return;
    setSelectedNovedadData(row);
    setOpenNovedadModal(true);
  }, []);

  const handleCloseNovedadModal = useCallback(() => {
    setOpenNovedadModal(false);
    setSelectedNovedadData(null);
  }, []);

  const handleNovedadSuccess = useCallback(() => {
    if (llaveSeleccionada && String(llaveSeleccionada).trim() !== "") {
      fetchByKey(String(llaveSeleccionada).trim());
    } else {
      fetchRecords();
    }
  }, [fetchByKey, fetchRecords, llaveSeleccionada]);

  // ================================================
  // CONCILIAR
  // ================================================
  const handleConciliar = useCallback((row) => {
    setConciliationRow(row);
  }, []);

  const handleCerrarAvisoConciliar = useCallback(() => setAvisoConciliar(null), []);

  // ================================================
  // EXPORTAR EXCEL
  // ================================================
  const exportToExcel = useCallback(() => {
    if (filteredRecords.length === 0) return;

    try {
      let exportData;
      let cols;

      if (isKeyMode) {
        exportData = filteredRecords.map((item) => ({
          ID: item.conciliation_id || item.id,
          Concesión: item.concesion,
          Nombre: item.name || item.realName,
          "Tipo de pago": paymentTypeLabel(item.tipo_pago),
          Vertical: item.vertical,
          "Fecha Conciliación": item.conciliation_date,
          "Key Concil": item.key_concil || "N/A",
          "Total Registros": item.countconcil,
          "Valor Gopass": valorGopass(item.value_gp),
          Status: conciliationStatus(item.status).label,
          Observación: item.observation,
          Creado: item.created_at ? new Date(item.created_at).toLocaleString() : "N/A",
        }));
        cols = [
          { wch: 10 },
          { wch: 12 },
          { wch: 30 },
          { wch: 18 },
          { wch: 15 },
          { wch: 18 },
          { wch: 20 },
          { wch: 15 },
          { wch: 20 },
          { wch: 12 },
          { wch: 40 },
          { wch: 22 },
        ];
      } else {
        exportData = filteredRecords.map((item) => ({
          ID: item.id,
          UUID: item.uuid || "N/A",
          Estado: conciliationStatus(item.status).label,
          Llave: item.key_concil || "N/A",
          "Fecha Creación": formatFecha(item.created_at),
        }));
        cols = [{ wch: 8 }, { wch: 40 }, { wch: 14 }, { wch: 16 }, { wch: 22 }];
      }

      const ws = XLSX.utils.json_to_sheet(exportData);
      ws["!cols"] = cols;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Conciliaciones");
      XLSX.writeFile(wb, `conciliaciones_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      console.error("Error al exportar:", err);
    }
  }, [filteredRecords, isKeyMode]);

  // ================================================
  // COLUMNAS Y FILAS
  // ================================================
  const AccionesCellWithHandlers = useCallback(
    ({ row }) => {
      return (
        <AccionesCell
          row={row}
          modo={isKeyMode ? "llave" : "global"}
          onVerDetalle={handleVerDetalle}
          onVerPDF={handleVerPDF}
          onFiltrar={handleFiltrar}
          onAgregarNovedad={handleAgregarNovedad}
          onConciliar={handleConciliar}
        />
      );
    },
    [
      isKeyMode,
      handleVerDetalle,
      handleVerPDF,
      handleFiltrar,
      handleAgregarNovedad,
      handleConciliar,
    ]
  );

  AccionesCellWithHandlers.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.object,
    }).isRequired,
  };

  const globalColumns = useMemo(
    () => [
      { Header: "ID", accessor: "id", width: "6%", Cell: IdConciliacionWrapper },
      { Header: "UUID", accessor: "uuid", width: "28%", Cell: UuidWrapper },
      { Header: "Estado", accessor: "status", width: "16%", Cell: StatusTextWrapper },
      { Header: "Llave", accessor: "key_concil", width: "16%", Cell: LlaveWrapper },
      {
        Header: "Fecha Creación",
        accessor: "created_at",
        width: "18%",
        Cell: FechaCreacionWrapper,
      },
      { Header: "Acciones", accessor: "acciones", width: "16%", Cell: AccionesCellWithHandlers },
    ],
    [AccionesCellWithHandlers]
  );

  const keyColumns = useMemo(
    () => [
      {
        Header: "Código",
        accessor: "concesion",
        width: "14.2857%",
        Cell: ConcesionWrapper,
        align: "center",
      },
      {
        Header: "Concesión",
        accessor: "name",
        width: "14.2857%",
        Cell: NombreWrapper,
        align: "center",
      },
      {
        Header: "Tipo de pago",
        accessor: "tipo_pago",
        width: "14.2857%",
        Cell: TipoPagoWrapper,
        align: "center",
      },
      {
        Header: "Registros",
        accessor: "countconcil",
        width: "14.2857%",
        Cell: CountConcilWrapper,
        align: "center",
      },
      {
        Header: "Valor Gopass",
        accessor: "value_gp",
        width: "14.2857%",
        Cell: ValorGopassWrapper,
        align: "center",
      },
      {
        Header: "Estado",
        accessor: "status",
        width: "14.2857%",
        Cell: StatusWrapper,
        align: "center",
      },
      {
        Header: "Acciones",
        accessor: "acciones",
        width: "14.2857%",
        Cell: AccionesCellWithHandlers,
        align: "center",
      },
    ],
    [AccionesCellWithHandlers]
  );

  const columns = isKeyMode ? keyColumns : globalColumns;

  const rows = useMemo(
    () =>
      filteredRecords.map((item) => ({
        id: item.conciliation_id || item.id,
        conciliation_id: item.conciliation_id || item.id,
        detailReference: item.detailReference,
        uuid: item.uuid,
        concesion: item.concesion,
        name: item.name || item.realName,
        realName: item.realName,
        tipo_pago: item.tipo_pago,
        more: item.more,
        vertical: item.vertical,
        conciliation_date: item.conciliation_date,
        key_concil: item.key_concil,
        countconcil: item.countconcil,
        value_gp: item.value_gp,
        status: item.status,
        estado_cuadre: item.estado_cuadre,
        valor_concesion: item.valor_concesion,
        value_final: item.value_final,
        observation: item.observation,
        created_at: item.created_at,
        original: item,
      })),
    [filteredRecords]
  );

  // ================================================
  // RENDER
  // ================================================
  return (
    <>
      <MDBox>
        <Card
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
          }}
        >
          <MDBox
            p={3}
            sx={{
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
            }}
          >
            <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
              <Grid item xs={12} md={6}>
                <MDTypography variant="h5" color="white" fontWeight="bold">
                  {isKeyMode ? "Concesiones de la llave" : "Lotes de conciliación"}
                </MDTypography>
                <MDTypography variant="body2" color="white" opacity={0.8}>
                  {totalFiltered.toLocaleString("es-CO")} {isKeyMode ? "concesiones" : "lotes"}
                  {filtroBusqueda && (
                    <span style={{ marginLeft: 8 }}>Búsqueda: {filtroBusqueda}</span>
                  )}
                  {llaveSeleccionada && (
                    <span style={{ marginLeft: 8 }}>Llave: {llaveSeleccionada}</span>
                  )}
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox display="flex" justifyContent="flex-end" alignItems="center" gap={2}>
                  <MDButton
                    variant="contained"
                    onClick={exportToExcel}
                    startIcon={<Download />}
                    disabled={filteredRecords.length === 0}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      bgcolor: colors.white,
                      color: colors.primary,
                      "&:hover": { bgcolor: "rgba(255,255,255,0.95)" },
                      "&:disabled": {
                        bgcolor: "rgba(255,255,255,0.3)",
                        color: "rgba(255,255,255,0.5)",
                      },
                    }}
                  >
                    Exportar Excel
                  </MDButton>
                  <MDButton
                    variant="contained"
                    onClick={() => {
                      if (llaveSeleccionada && String(llaveSeleccionada).trim() !== "") {
                        fetchByKey(String(llaveSeleccionada).trim());
                      } else {
                        fetchRecords();
                      }
                    }}
                    disabled={loading}
                    startIcon={<Refresh />}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 600,
                      bgcolor: colors.white,
                      color: colors.primary,
                      "&:hover": { bgcolor: "rgba(255,255,255,0.95)" },
                    }}
                  >
                    {loading ? "Cargando..." : "Actualizar"}
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>

          {isKeyMode && keySummary && (
            <Box
              sx={{
                px: 3,
                py: 2.5,
                borderBottom: 1,
                borderColor: "divider",
                bgcolor: "background.default",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, minmax(0, 1fr))" },
                  gap: 2,
                }}
              >
                {[
                  [
                    "Registros conciliados",
                    keySummary.totalRegistros.toLocaleString("es-CO"),
                    "#2563eb",
                    "rgba(37,99,235,0.08)",
                  ],
                  [
                    "Valor total",
                    formatMoney(keySummary.valorTotal),
                    "#15803d",
                    "rgba(21,128,61,0.08)",
                  ],
                  [
                    "Novedades · Abonos",
                    formatMoney(keySummary.totalAbonoNov),
                    "#0f766e",
                    "rgba(15,118,110,0.08)",
                  ],
                  [
                    "Novedades · Ajustes",
                    formatMoney(keySummary.totalAjusteNov),
                    "#b45309",
                    "rgba(180,83,9,0.08)",
                  ],
                ].map(([label, value, accent, tint]) => (
                  <Box
                    key={label}
                    sx={{
                      px: 2,
                      py: 1.5,
                      border: 1,
                      borderColor: accent,
                      borderTop: "3px solid",
                      borderTopColor: accent,
                      borderRadius: 2,
                      bgcolor: tint,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 12, color: "text.primary", fontWeight: 500, mb: 0.75 }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: "text.primary",
                        fontVariantNumeric: "tabular-nums",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
              {keySummary.conciliationId && (
                <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1.5 }}>
                  ID de conciliación: {keySummary.conciliationId}
                </Typography>
              )}
            </Box>
          )}
          {loading ? (
            <MDBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              minHeight="300px"
              gap={2}
              p={3}
            >
              <CircularProgress sx={{ color: colors.secondary }} />
              <MDTypography variant="h6" color="text.secondary" fontWeight="bold">
                Cargando conciliaciones...
              </MDTypography>
              <MDTypography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                maxWidth={460}
              >
                ⏱️ Este proceso puede tardar hasta <strong>3 minutos</strong> en responder.
                <br />
                Por favor <strong>no cierres ni recargues la página</strong> mientras se completa la
                consulta.
              </MDTypography>
              <Chip
                icon={<CircularProgress size={14} sx={{ color: "#fff !important" }} />}
                label="Procesando en el servidor"
                sx={{
                  bgcolor: colors.info,
                  color: "#fff",
                  fontWeight: 600,
                  "& .MuiChip-label": { px: 2 },
                }}
              />
            </MDBox>
          ) : error ? (
            <Alert severity="error" sx={{ m: 3 }}>
              <MDTypography variant="body2" fontWeight="bold">
                Error al cargar los datos
              </MDTypography>
              <MDTypography variant="caption">{error}</MDTypography>
            </Alert>
          ) : totalFiltered === 0 ? (
            <MDBox p={5} textAlign="center">
              <MDTypography variant="h6" sx={{ color: "#64748b" }}>
                {filtroBusqueda
                  ? "No se encontraron resultados"
                  : llaveSeleccionada
                  ? `No se encontraron conciliaciones para la llave ${llaveSeleccionada}`
                  : "No hay conciliaciones disponibles"}
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: "#94a3b8", mt: 1 }}>
                {filtroBusqueda
                  ? `No se encontraron coincidencias para "${filtroBusqueda}"`
                  : llaveSeleccionada
                  ? "Verifique que la llave seleccionada tenga conciliaciones registradas"
                  : "Ajuste los filtros para ver resultados"}
              </MDTypography>
            </MDBox>
          ) : (
            <>
              <Box
                sx={{
                  "& thead th": {
                    bgcolor: "background.default",
                    py: 1.5,
                    ...(isKeyMode ? { width: "14.2857%", px: 1.5 } : {}),
                  },
                  "& thead th > div": { opacity: 1, color: "text.primary" },
                  "& tbody td": {
                    py: 1.5,
                    px: isKeyMode ? 1.5 : 2,
                    verticalAlign: "middle",
                    ...(isKeyMode ? { textAlign: "center" } : {}),
                  },
                  "& tbody td .MuiTypography-root": {
                    color: (theme) =>
                      theme.palette.mode === "dark" ? "#f1f5f9 !important" : "#111827 !important",
                    fontWeight: "600 !important",
                    opacity: "1 !important",
                  },
                  ...(isKeyMode
                    ? {
                        "& tbody td > div": { width: "100%", maxWidth: "100%" },
                        "& tbody td > div > .MuiTypography-root": {
                          color: (theme) =>
                            theme.palette.mode === "dark"
                              ? "#f1f5f9 !important"
                              : "#111827 !important",
                          fontWeight: "600 !important",
                          opacity: "1 !important",
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                        },
                        "& tbody td:last-child > div > div": { justifyContent: "center" },
                      }
                    : {}),
                  "& tbody tr:nth-of-type(even)": { bgcolor: "action.hover" },
                  "& tbody td span, & tbody td p": { fontSize: "13px" },
                  "& table": {
                    minWidth: isKeyMode ? 1200 : 1080,
                    ...(isKeyMode ? { tableLayout: "fixed", width: "100%" } : {}),
                  },
                }}
              >
                <DataTable
                  key={isKeyMode ? "tabla-llave" : "tabla-global"}
                  table={{ columns, rows }}
                  isSorted={true}
                  entriesPerPage={{ defaultValue: 10, entries: [10, 25, 50, 100] }}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={false}
                  pagination={{ variant: "outlined", color: "info" }}
                  paginationResetKey={JSON.stringify([
                    llaveSeleccionada,
                    fechaInicio,
                    fechaFin,
                    filtroVertical,
                    filtroStatus,
                    filtroBusqueda,
                    refreshFlag,
                  ])}
                />
              </Box>
            </>
          )}
        </Card>
      </MDBox>

      {/* MODAL DETALLE */}
      <Dialog
        open={openDetalle}
        onClose={handleCloseDetalle}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
            overflow: "hidden",
            width: "95%",
            maxWidth: "95%",
            height: "auto",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 2,
            pt: 3,
            px: 4,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <MDTypography variant="h5" fontWeight="bold">
              Detalle de Conciliación:{" "}
              {selectedLoteData?.name || selectedLoteData?.realName || selectedLoteData?.id}
            </MDTypography>
            {(selectedLoteData?.concesion || selectedLoteData?.vertical) && (
              <Chip
                label={selectedLoteData?.concesion || selectedLoteData?.vertical}
                size="medium"
                sx={{
                  fontWeight: 600,
                  backgroundColor: `${colors.success}15`,
                  color: colors.success,
                  fontSize: "0.9rem",
                }}
              />
            )}
            {selectedLoteData?.key_concil && (
              <Chip
                label={`Llave: ${selectedLoteData.key_concil}`}
                size="small"
                sx={{
                  fontWeight: 600,
                  backgroundColor: `${colors.info}15`,
                  color: colors.info,
                  fontFamily: "monospace",
                }}
              />
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <MDTypography variant="caption" color="text.secondary">
              ID: {selectedLoteData?.conciliation_id || selectedLoteData?.id}
            </MDTypography>
            <IconButton onClick={handleCloseDetalle} size="large">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, overflow: "auto" }}>
          <TableDetalleFinanciera1
            valorFinalFinops={selectedLoteData?.value_final}
            showLiquidationKeys
            conciliationId={selectedLoteData?.conciliation_id || selectedLoteData?.id}
            detailReference={selectedLoteData?.detailReference}
            concesion={
              selectedLoteData?.concesion || selectedLoteData?.vertical || selectedLoteData?.code_op
            }
            llaveConciliacion={selectedLoteData?.key_concil || selectedLoteData?.llave || null}
          />
        </DialogContent>
      </Dialog>

      {/* MODAL PDF / DOCUMENTOS */}
      <ModalPDF
        open={openPDFModal}
        onClose={handleClosePDFModal}
        keyConcil={selectedKeyConcil}
        filesData={selectedFilesData}
      />

      <ModalAgregarNovedad
        open={openNovedadModal && !novedadesBloqueadas(selectedNovedadData)}
        onClose={handleCloseNovedadModal}
        onSuccess={handleNovedadSuccess}
        onError={onError}
        keyConcil={selectedNovedadData?.key_concil || selectedNovedadData?.llave || null}
        code={selectedNovedadData?.concesion || null}
        conciliationName={selectedNovedadData?.name || selectedNovedadData?.realName || null}
      />

      {conciliationRow && (
        <ModalConciliar
          row={conciliationRow}
          onClose={() => setConciliationRow(null)}
          onSuccess={() => {
            setConciliationRow(null);
            setAvisoConciliar({
              severity: "success",
              message: "El valor a pagar se envió a Financiera.",
            });
            handleNovedadSuccess();
          }}
        />
      )}

      <Snackbar
        open={Boolean(avisoConciliar)}
        autoHideDuration={5000}
        onClose={handleCerrarAvisoConciliar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={avisoConciliar?.severity || "info"}
          onClose={handleCerrarAvisoConciliar}
          sx={{ width: "100%", borderRadius: 2 }}
          variant="filled"
        >
          {avisoConciliar?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

TableLoteFinanciera1.propTypes = {
  onStatusOptionsChange: PropTypes.func,
  refreshFlag: PropTypes.bool,
  onError: PropTypes.func,
  onSelectLote: PropTypes.func,
  onFiltrarLlave: PropTypes.func,
  onVerExcelLlave: PropTypes.func,
  excelByKeyConcil: PropTypes.object,
  llaveSeleccionada: PropTypes.string,
  fechaInicio: PropTypes.string,
  fechaFin: PropTypes.string,
  filtroVertical: PropTypes.string,
  filtroBusqueda: PropTypes.string,
  filtroStatus: PropTypes.string,
  tipoFiltro: PropTypes.string,
  onConciliar: PropTypes.func,
};

TableLoteFinanciera1.defaultProps = {
  refreshFlag: false,
  onError: null,
  onSelectLote: null,
  onFiltrarLlave: null,
  onVerExcelLlave: null,
  excelByKeyConcil: {},
  llaveSeleccionada: "",
  fechaInicio: "",
  fechaFin: "",
  filtroVertical: "",
  filtroBusqueda: "",
  filtroStatus: "todos",
  tipoFiltro: "global",
  onConciliar: null,
};

export default TableLoteFinanciera1;
